import { Colors, useSlideLayout } from '@gripp/shared-ui';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useBlocker, type BlockerFunction } from 'react-router-dom';

interface UseUnsavedChangesBlockerOptions {
  isDirty: boolean;
  isValid: boolean;
  onSave: () => Promise<void>;
}

export const useUnsavedChangesBlocker = ({
  isDirty,
  isValid,
  onSave,
}: UseUnsavedChangesBlockerOptions) => {
  const { t } = useTranslation();
  const { setColumnOverlay } = useSlideLayout();

  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Immediately clears the dirty ref so that a navigate() call fired
  // synchronously afterwards is not intercepted by the blocker.
  // Use this when the caller has already handled the "discard changes"
  // confirmation and wants to navigate without triggering the blocker overlay.
  const clearDirtyImmediate = useCallback(() => {
    isDirtyRef.current = false;
  }, []);

  const blockerFn = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      isDirtyRef.current && currentLocation.pathname !== nextLocation.pathname,
    []
  );
  const blocker = useBlocker(blockerFn);

  const handleSaveAndExit = useCallback(async () => {
    if (!isValid) return;
    await onSave();
    blocker.proceed?.();
  }, [onSave, blocker, isValid]);

  const handleDontSave = useCallback(() => {
    blocker.proceed?.();
  }, [blocker]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setColumnOverlay(
        <View style={styles.overlay}>
          <View style={styles.sheet}>
            <Text style={styles.title}>{t('unsavedChanges.title')}</Text>
            <Text style={styles.subtitle}>{t('unsavedChanges.subtitle')}</Text>
            <TouchableOpacity
              style={styles.saveButton}
              onPress={handleSaveAndExit}
            >
              <Text style={styles.saveButtonText}>
                {t('unsavedChanges.saveAndExit')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dontSaveButton}
              onPress={handleDontSave}
            >
              <Text style={styles.dontSaveText}>
                {t('unsavedChanges.dontSave')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    } else {
      setColumnOverlay(null);
    }
    return () => {
      setColumnOverlay(null);
    };
  }, [blocker.state, handleSaveAndExit, handleDontSave, setColumnOverlay, t]);

  return { clearDirtyImmediate };
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: Colors.role.scrim,
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Colors.role.surface,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
    color: Colors.role.textStrong,
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    color: Colors.role.textSecondary,
    textAlign: 'center',
    marginBottom: 20,
  },
  saveButton: {
    backgroundColor: Colors.role.surfaceInverse,
    borderRadius: 2,
    height: 48,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  saveButtonText: {
    color: Colors.role.textOnInverse,
    fontSize: 16,
    fontWeight: '600',
  },
  dontSaveButton: {
    height: 48,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: Colors.role.borderControl,
    borderRadius: 2,
  },
  dontSaveText: {
    fontSize: 16,
    color: Colors.role.textStrong,
  },
});
